import Loading from 'core-system/Loading'
import PageHeader from 'core-system/PageHeader'
import Notifier from 'features/Notifications/Notifier'
import SidebarView from 'features/Sidebar/SidebarView'
import React, { useEffect, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { AppState } from 'redux/config/store'
import { userActions } from 'redux/user/userSlice'
import useCurrentPath from 'shared/Hooks/useCurrentPath'
import refExists from 'shared/refExists'
import styled from 'styled-components'
import isTokenExpired from '../isTokenExpired'
import { isTokenExpiringSoon } from 'shared/auth/tokenStorage'
import { refreshAccessToken } from 'shared/auth/tokenRefresh'
import { locationData, Locations } from './Locations'
import { employeePlatformActions } from 'redux/employeePlatform/employeePlatformSlice'
import { applicationActions } from 'redux/application/applicationSlice'
import ConciergeChat from 'features/Concierge-AI/ConciergeChat'
import { useGateValue } from '@statsig/react-bindings'
import { isOnboarding } from 'features/Onboarding/helperFunctions'
import { sessionBootstrap } from './sessionBootstrap'
import { getPlatformType } from './Utils'
import { useStatsigUser } from '@statsig/react-bindings'
import ErrorView from 'features/ErrorView'

const Page = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  position: relative;
  background-color: ${(props) => props.theme.palette.white};
`

const Body = styled.div<{
  noPadding?: boolean
  showVerticalScroll?: boolean
}>`
  display: flex;
  flex: 1 1;
  height: 100%;
  width: 100%;
  flex-direction: column;
  align-items: center;
  padding: ${(props) => (props.noPadding ? 0 : '2.5rem')};
  margin: 0 auto;
  z-index: ${(props) => props.theme.zIndex.body};
  overflow-y: ${(props) => (props.showVerticalScroll ? 'scroll' : 'auto')};

  @media (max-width: ${(props) => props.theme.breakpoints[2]}) {
    padding: ${(props) => (props.noPadding ? 0 : '2.5rem 1.5rem')};
  }
`

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;

  // This is to prevent the container from overlapping the sidebar
  min-width: 0;

  @media (max-width: ${(props) => props.theme.breakpoints[1]}) {
    max-width: 100%;
  }
`

const Inner = styled.div<{ noMax: boolean }>`
  max-width: ${(props) => (props.noMax ? 'unset' : '75rem')};
  width: 100%;
`

const DefaultLayout = ({
  showSidebar = true,
  showHeaderNav = true,
}: {
  showSidebar?: boolean
  showHeaderNav?: boolean
}) => {
  const [sessionReady, setSessionReady] = React.useState(false)
  const isExpired = isTokenExpired()
  const dispatch = useDispatch()
  const location = useLocation()
  const scrollRef = useRef<HTMLDivElement>(null)
  const path = useCurrentPath()
  const navigate = useNavigate()
  const { updateUserAsync } = useStatsigUser()

  const platformType = getPlatformType()

  const authUserFail = useSelector((state: AppState) => state.user.authUserFail)
  const userId = useSelector((state: AppState) => state.user.profile.id)
  const { profileData, noProfileData } = useSelector(
    (state: AppState) => state.employeePlatform
  )
  const { profile: employerProfile } = useSelector(
    (state: AppState) => state.user
  )
  const allSegments = useSelector(
    (state: AppState) => state.employer.allSegments
  )
  const worksites = useSelector((state: AppState) => state.employer.worksites)
  const programsLoaded = useSelector(
    (state: AppState) => state.programs.programsLoaded
  )
  const merchants = useSelector((state: AppState) => state.programs.merchants)
  const allIncentives = useSelector(
    (state: AppState) => state.incentives.allIncentives
  )
  const apiLoading = useSelector(
    (state: AppState) => state.application.apiLoading
  )
  const leaderboardsMap = useSelector(
    (state: AppState) => state.leaderboards.leaderboardsMap
  )
  const onboarding = useSelector((state: AppState) => state.onboarding)

  const employeeConciergeEnabled = useGateValue('ai_concierge_employee')
  const adminConciergeEnabled = useGateValue('ai_concierge_admin')
  const canUseConcierge =
    (platformType === 'employee' && employeeConciergeEnabled) ||
    (platformType === 'employer' &&
      adminConciergeEnabled &&
      (employerProfile?.groups || []).includes('HRadmins'))

  const updateUserOnStatsig = async () => {
    let userObject = {}
    if (
      profileData &&
      profileData.id !== undefined &&
      platformType === 'employee'
    ) {
      userObject = {
        userID: profileData.id,
        email: profileData.email,
        customIDs: {
          employerID: profileData.employerId,
          worksiteID: profileData.worksiteId,
        },
        custom: {
          companyName: profileData.name,
          segmentNameList: profileData.segments.map((segment) => segment.name),
        },
      }
    }
    if (
      employerProfile &&
      employerProfile.id !== undefined &&
      platformType === 'employer'
    ) {
      userObject = {
        userID: employerProfile.id,
        email: employerProfile.email,
        customIDs: {
          worksiteID: employerProfile.worksiteId,
        },
      }
    }
    if (Object.keys(userObject).length > 0) {
      await updateUserAsync(userObject)
    }
  }

  useEffect(() => {
    updateUserOnStatsig()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileData, employerProfile])

  /*
    Navigating to dashboard if the employee is onboarding
    This is only for async items onboarding

    For employer we need to redirect them to different pages based on the onboarding stage
  */
  useEffect(() => {
    if (isOnboarding(onboarding)) {
      if (platformType === 'employee') {
        navigate(Locations.EmployeeDashboard)
      }
    }
  }, [onboarding, platformType])

  useEffect(() => {
    let cancelled = false

    const ensureSession = async () => {
      if (authUserFail) {
        if (!cancelled) {
          setSessionReady(true)
        }
        return
      }

      if (isTokenExpired() || isTokenExpiringSoon()) {
        const refreshed = await refreshAccessToken()
        if (!refreshed && !cancelled) {
          dispatch(userActions.logout())
        }
      }

      if (!cancelled) {
        setSessionReady(true)
      }
    }

    ensureSession()

    return () => {
      cancelled = true
    }
  }, [authUserFail, dispatch])

  useEffect(() => {
    if (!sessionReady) {
      return
    }

    if (isExpired || authUserFail) {
      dispatch(userActions.logout())
      return
    }
    // The Okta tile stores the HR user before this layout mounts. The employee
    // dashboard still has to load /mobile/profile/me/ or the page stays on the loader.
    const bootstrap = sessionBootstrap({
      sessionReady,
      isExpired,
      authUserFail,
      platformType,
      userId,
      hasEmployeeProfile: Boolean(profileData),
      employeeProfileFailed: noProfileData,
    })
    if (bootstrap.fetchProperties) {
      dispatch(applicationActions.fetchProperties())
    }
    if (bootstrap.loadEmployeeProfile) {
      dispatch(employeePlatformActions.getProfileData())
    }
    if (bootstrap.loadHrUser) {
      dispatch(userActions.getUser())
    }
  }, [
    sessionReady,
    isExpired,
    authUserFail,
    userId,
    platformType,
    profileData,
    noProfileData,
    dispatch,
  ])

  // Scroll to top of page
  useEffect(() => {
    if (refExists(scrollRef.current)) {
      scrollRef.current.scrollTop = 0
    }
  }, [location.pathname])

  if (!sessionReady) {
    return <Loading fullPage withoutNav />
  }

  if (isExpired || authUserFail) {
    return <Navigate to={Locations.Login} replace />
  }

  if (location?.pathname === '/') {
    if (platformType === 'employee') {
      return <Navigate replace to={Locations.EmployeeDashboard} />
    } else {
      return <Navigate replace to={Locations.Dashboard} />
    }
  }

  /*
    If there is no profile data, show the error view
    This will only happen if an admin (which is only admin not employee) accidentally access the employee platform
  */
  if (noProfileData && platformType === 'employee') {
    return (
      <ErrorView
        showLogoutButton
        errorMessage='You accidentally accessed the employee platform. Please logout and access the admin platform again.'
      />
    )
  }

  if (
    ((!allSegments ||
      !programsLoaded ||
      !allIncentives ||
      !leaderboardsMap ||
      !worksites ||
      !merchants) &&
      platformType === 'employer') ||
    (platformType === 'employee' && !profileData)
  ) {
    return <Loading fullPage withoutNav />
  }

  // if (worksites && worksites.length === 0) {
  //   return <Navigate to={Locations.Onboarding} replace />
  // }

  const { noPadding, showVerticalScroll, ignoreMaxWidth } = locationData[path]

  return (
    <>
      {apiLoading && <Loading fullPage withoutNav hasOpacity />}
      <Notifier />
      <Page id='page'>
        {showSidebar && <SidebarView platformType={platformType} />}
        <Container>
          {showHeaderNav && <PageHeader platformType={platformType} />}
          <Body
            id='page-main-body'
            ref={scrollRef}
            noPadding={noPadding}
            showVerticalScroll={showVerticalScroll}
          >
            <Inner noMax={ignoreMaxWidth}>
              <Outlet />
            </Inner>
          </Body>
        </Container>
        {canUseConcierge && <ConciergeChat />}
      </Page>
    </>
  )
}

DefaultLayout.displayName = 'DefaultLayout'

export default DefaultLayout
